import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { setUserRole } from '../../utils/roleManager';
import { ROLES } from '../../utils/roleManager';
import { JOB_ROLES } from '../../data/nurseAppModels';
import { isInAppBrowser, shouldShowBrowserPrompt, shouldAutoRedirect, openInExternalBrowser } from '../../utils/browserDetector';
import { 
  createUserWithEmailAndPassword, 
  signInWithPopup,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  updateProfile,
  signOut
} from 'firebase/auth';
import { auth, provider } from '../../firebase/firebaseConfig';
import { doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

// Add CSS animation for logo
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.setAttribute('data-logo-animation-register', 'true');
  styleSheet.innerText = `
    @keyframes logoFloat {
      0%, 100% {
        transform: translateY(0px) rotate(0deg);
      }
      25% {
        transform: translateY(-10px) rotate(2deg);
      }
      50% {
        transform: translateY(-20px) rotate(0deg);
      }
      75% {
        transform: translateY(-10px) rotate(-2deg);
      }
    }
    
    .logo-hero {
      animation: logoFloat 6s ease-in-out infinite;
    }
  `;
  if (!document.head.querySelector('style[data-logo-animation-register]')) {
    document.head.appendChild(styleSheet);
  }
}

// Check if in development mode
const isDevelopment = process.env.NODE_ENV === 'development';

const RegisterNew = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, refreshUser } = useAuth();
  const { currentTheme } = useTheme();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Register method selection
  const [registerMethod, setRegisterMethod] = useState('phone'); // 'phone' or 'google'
  const [phoneVerified, setPhoneVerified] = useState(false); // Track if phone is verified
  const [phoneAuthResult, setPhoneAuthResult] = useState(null); // Store phone auth result for later use

  // Step 1: Phone Number & OTP (ถ้าเลือก phone) หรือ Google Sign-In (ถ้าเลือก google)
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const recaptchaVerifierRef = useRef(null);
  const recaptchaContainerRef = useRef(null);

  // Step 2: Personal Info + Role Selection + Store Name (รวม Step 2+3)
  const [formData, setFormData] = useState({
    displayName: '',
    email: '', // Optional
    password: '', // Optional
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [selectedRole, setSelectedRole] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [newsletter, setNewsletter] = useState(false);

  // Password strength calculator
  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 1;
    return strength;
  };

  const handlePasswordChange = (e) => {
    const password = e.target.value;
    setFormData({ ...formData, password });
    setPasswordStrength(calculatePasswordStrength(password));
  };

  // Initialize reCAPTCHA when component mounts
  useEffect(() => {
    // Only initialize if we're on step 1 (phone verification)
    if (currentStep === 1 && !recaptchaVerifierRef.current) {
      // Create a hidden container for reCAPTCHA if it doesn't exist
      if (!recaptchaContainerRef.current) {
        const container = document.createElement('div');
        container.id = 'recaptcha-container';
        container.style.display = 'none';
        document.body.appendChild(container);
        recaptchaContainerRef.current = container;
      }

      try {
        recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', {
          'size': 'invisible',
          'callback': (response) => {
            console.log('reCAPTCHA solved');
          },
          'expired-callback': () => {
            console.log('reCAPTCHA expired');
            // Re-initialize if expired
            if (recaptchaVerifierRef.current) {
              recaptchaVerifierRef.current.clear();
              recaptchaVerifierRef.current = null;
            }
          }
        });
      } catch (error) {
        console.error('Error initializing reCAPTCHA:', error);
      }
    }

    return () => {
      // Cleanup on unmount
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (error) {
          console.error('Error clearing reCAPTCHA:', error);
        }
        recaptchaVerifierRef.current = null;
      }
    };
  }, [currentStep]);

  // Send OTP with Firebase Phone Authentication
  const handleSendOTP = async () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      alert('กรุณากรอกหมายเลขโทรศัพท์ให้ถูกต้อง');
      return;
    }

    setLoading(true);
    try {
      // Format phone number (add +66 for Thailand)
      const formattedPhone = phoneNumber.startsWith('0') 
        ? `+66${phoneNumber.substring(1)}` 
        : phoneNumber.startsWith('+66')
        ? phoneNumber
        : `+66${phoneNumber}`;

      console.log('Sending OTP to:', formattedPhone);

      // Check if in development mode and using test phone numbers
      if (isDevelopment) {
        // In dev mode, try to use Firebase Phone Auth
        // If phone is in test numbers, Firebase will use the test code
        // Otherwise, it will send real SMS (but we can catch and handle it)
        try {
          if (!recaptchaVerifierRef.current) {
            // Re-initialize if needed
            if (!recaptchaContainerRef.current) {
              const container = document.createElement('div');
              container.id = 'recaptcha-container';
              container.style.display = 'none';
              document.body.appendChild(container);
              recaptchaContainerRef.current = container;
            }
            recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', {
              'size': 'invisible'
            });
          }

          const confirmation = await signInWithPhoneNumber(
            auth,
            formattedPhone,
            recaptchaVerifierRef.current
          );
          
          setConfirmationResult(confirmation);
          setOtpSent(true);
          setOtpTimer(300); // 5 minutes
          setLoading(false);
          
          // Check if phone is in test numbers (Firebase will use test code)
          // Show appropriate message
          alert('ส่ง OTP เรียบร้อยแล้ว!\n\n' + 
            '💡 Dev Mode:\n' +
            '- ถ้าเบอร์อยู่ใน Phone numbers for testing → ใช้รหัสทดสอบ\n' +
            '- ถ้าเบอร์ไม่อยู่ใน test numbers → ตรวจสอบ SMS หรือใช้รหัส 123456 (demo mode)');
        } catch (error) {
          console.error('Firebase Phone Auth error:', error);
          
          // In production, don't allow demo mode - show error instead
          if (!isDevelopment) {
            let errorMessage = 'เกิดข้อผิดพลาดในการส่ง OTP';
            
            if (error.code === 'auth/invalid-phone-number') {
              errorMessage = 'หมายเลขโทรศัพท์ไม่ถูกต้อง';
            } else if (error.code === 'auth/too-many-requests') {
              errorMessage = 'ส่ง OTP มากเกินไป กรุณารอสักครู่';
            } else if (error.code === 'auth/quota-exceeded') {
              errorMessage = 'เกิน quota SMS แล้ว กรุณาติดต่อผู้ดูแลระบบ';
            } else if (error.code === 'auth/operation-not-allowed') {
              errorMessage = 'Phone Authentication ยังไม่ได้เปิดใช้งาน กรุณาติดต่อผู้ดูแลระบบ';
            } else {
              errorMessage = `เกิดข้อผิดพลาด: ${error.message}`;
            }
            
            alert(errorMessage);
            setLoading(false);
            return;
          }
          
          // Fallback to demo mode only in development
          if (error.code === 'auth/too-many-requests' || error.code === 'auth/quota-exceeded') {
            alert('⚠️ เกิน quota SMS แล้ว ใช้โหมด Demo (รหัส: 123456)');
            setOtpSent(true);
            setOtpTimer(300);
            setLoading(false);
          } else {
            // For other errors, show the error but allow demo mode
            console.warn('Using demo mode due to error:', error.message);
            alert(`⚠️ ${error.message}\nใช้โหมด Demo (รหัส: 123456)`);
            setOtpSent(true);
            setOtpTimer(300);
            setLoading(false);
          }
        }
      } else {
        // Production mode - always use Firebase Phone Auth
        if (!recaptchaVerifierRef.current) {
          if (!recaptchaContainerRef.current) {
            const container = document.createElement('div');
            container.id = 'recaptcha-container';
            container.style.display = 'none';
            document.body.appendChild(container);
            recaptchaContainerRef.current = container;
          }
          recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', {
            'size': 'invisible'
          });
        }

        const confirmation = await signInWithPhoneNumber(
          auth,
          formattedPhone,
          recaptchaVerifierRef.current
        );
        
        setConfirmationResult(confirmation);
        setOtpSent(true);
        setOtpTimer(300);
        setLoading(false);
        alert('ส่ง OTP เรียบร้อยแล้ว! กรุณาตรวจสอบ SMS');
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      let errorMessage = 'เกิดข้อผิดพลาดในการส่ง OTP';
      
      if (error.code === 'auth/invalid-phone-number') {
        errorMessage = 'หมายเลขโทรศัพท์ไม่ถูกต้อง';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'ส่ง OTP มากเกินไป กรุณารอสักครู่';
      } else if (error.code === 'auth/quota-exceeded') {
        errorMessage = 'เกิน quota SMS แล้ว กรุณาติดต่อผู้ดูแลระบบ';
      } else {
        errorMessage = `เกิดข้อผิดพลาด: ${error.message}`;
      }
      
      alert(errorMessage);
      setLoading(false);
    }
  };

  // Verify OTP
  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      alert('กรุณากรอก OTP ให้ครบ 6 หลัก');
      return;
    }

    setLoading(true);
    try {
      // If we have confirmationResult from Firebase Phone Auth, verify with Firebase
      if (confirmationResult) {
        try {
          const result = await confirmationResult.confirm(otp);
          // OTP verified successfully - Firebase จะ sign in ทันที
          
          // เช็คว่าเบอร์โทรนี้เคยสมัครแล้วหรือยัง
          try {
            const userRef = doc(db, 'users', result.user.uid);
            const userSnap = await getDoc(userRef);
            
            if (userSnap.exists()) {
              // เบอร์โทรนี้เคยสมัครแล้ว → redirect ไปหน้า Login
              alert('เบอร์โทรศัพท์นี้เคยสมัครแล้ว กรุณาเข้าสู่ระบบ');
              // Sign out เพื่อให้ user login ใหม่
              await signOut(auth);
              navigate('/login');
              setLoading(false);
              return;
            }
          } catch (checkError) {
            console.error('Error checking user document:', checkError);
            // ถ้าเช็คไม่ได้ ให้ดำเนินการต่อ (อาจเป็น error ชั่วคราว)
          }
          
          // เก็บ phone auth result ไว้สำหรับสร้าง account
          setPhoneAuthResult(result);
          setPhoneVerified(true);
          setCurrentStep(2); // ไป Step 2 (รวม Step 2+3)
          setOtpTimer(0);
          setOtp('');
          setOtpSent(false);
          setLoading(false);
          // User ถูก sign in แล้วด้วย Phone Authentication
        } catch (error) {
          console.error('OTP verification error:', error);
          if (error.code === 'auth/invalid-verification-code') {
            alert('OTP ไม่ถูกต้อง กรุณาลองอีกครั้ง');
          } else if (error.code === 'auth/code-expired') {
            alert('OTP หมดอายุแล้ว กรุณาขอ OTP ใหม่');
            setOtpSent(false);
            setOtp('');
          } else {
            alert(`เกิดข้อผิดพลาด: ${error.message}`);
          }
          setLoading(false);
        }
      } else {
        // Fallback to demo mode (for development/testing)
        if (isDevelopment && otp === '123456') {
          // ใน demo mode ยังไม่ sign in จริง ต้องสร้าง account ตอน submit
          // แต่ถ้าเบอร์เป็น 0812345678 (demo) ให้ข้ามการเช็ค
          if (phoneNumber === '0812345678') {
            setPhoneVerified(true);
            setCurrentStep(2);
            setOtpTimer(0);
            setOtp('');
            setOtpSent(false);
            setLoading(false);
          } else {
            // สำหรับเบอร์อื่นใน demo mode - เช็คว่าเคยสมัครแล้วหรือยัง
            try {
              const formattedPhone = phoneNumber.startsWith('0') 
                ? `+66${phoneNumber.substring(1)}` 
                : phoneNumber.startsWith('+66')
                ? phoneNumber
                : `+66${phoneNumber}`;
              
              const usersQuery = query(
                collection(db, 'users'),
                where('phoneNumber', '==', formattedPhone)
              );
              const snapshot = await getDocs(usersQuery);
              
              if (!snapshot.empty) {
                // เบอร์โทรนี้เคยสมัครแล้ว
                alert('เบอร์โทรศัพท์นี้เคยสมัครแล้ว กรุณาเข้าสู่ระบบ');
                navigate('/login');
                setLoading(false);
                return;
              }
              
              // ยังไม่เคยสมัคร → ไป Step 2
              setPhoneVerified(true);
              setCurrentStep(2);
              setOtpTimer(0);
              setOtp('');
              setOtpSent(false);
              setLoading(false);
            } catch (checkError) {
              console.error('Error checking user in demo mode:', checkError);
              // ถ้าเช็คไม่ได้ ให้ดำเนินการต่อ
              setPhoneVerified(true);
              setCurrentStep(2);
              setOtpTimer(0);
              setOtp('');
              setOtpSent(false);
              setLoading(false);
            }
          }
        } else {
          alert('OTP ไม่ถูกต้อง กรุณาลองอีกครั้ง');
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      alert('เกิดข้อผิดพลาดในการยืนยัน OTP');
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (currentStep === 2) {
      // Validate Step 2 (รวม Step 2+3)
      if (!formData.displayName) {
        alert('กรุณากรอกชื่อ-นามสกุล');
        return;
      }

      if (!selectedRole) {
        alert('กรุณาเลือกบทบาทของคุณ');
        return;
      }

      if (!termsAccepted) {
        alert('กรุณายอมรับข้อกำหนดการใช้งาน');
        return;
      }

      // ถ้าใช้ phone registration - ตรวจสอบว่า phone verified แล้ว
      if (registerMethod === 'phone' && !phoneVerified) {
        alert('กรุณายืนยันเบอร์โทรศัพท์ก่อน');
        setCurrentStep(1);
        return;
      }

      // ถ้าใช้ phone registration และมี password - ตรวจสอบ password
      if (registerMethod === 'phone' && formData.password) {
        if (formData.password !== formData.confirmPassword) {
          alert('รหัสผ่านไม่ตรงกัน');
          return;
        }

        if (passwordStrength < 3 && formData.password.length > 0) {
          alert('รหัสผ่านไม่แข็งแรงพอ กรุณาใช้รหัสผ่านที่แข็งแรงกว่า');
          return;
        }
      }

      // Create account
      setLoading(true);
      try {
        let userCredential;
        
        // ถ้าใช้ Google registration - user ถูก sign in แล้วจาก handleGoogleSignUp
        if (registerMethod === 'google' && currentUser) {
          // ใช้ currentUser ที่ sign in ด้วย Google แล้ว
          userCredential = { user: currentUser };
        } else if (registerMethod === 'phone') {
          // Phone registration - ใช้ phone auth result
          if (phoneAuthResult) {
            // User ถูก sign in แล้วด้วย Phone Authentication
            userCredential = { user: phoneAuthResult.user };
            
            // Update display name
            if (formData.displayName) {
              await updateProfile(phoneAuthResult.user, {
                displayName: formData.displayName
              });
            }
          } else if (isDevelopment && phoneVerified) {
            // Demo mode - ต้องสร้าง account ด้วย email/password (fallback)
            // แต่ถ้าไม่มี email/password ให้ใช้ phone number เป็น email
            const formattedPhone = phoneNumber.startsWith('0') 
              ? `+66${phoneNumber.substring(1)}` 
              : phoneNumber.startsWith('+66')
              ? phoneNumber
              : `+66${phoneNumber}`;
            
            // ใช้ phone number เป็น email (สำหรับ demo mode)
            const demoEmail = `${formattedPhone.replace(/\+/g, '')}@cannatrade.demo`;
            const demoPassword = formData.password || 'Demo123!@#';
            
            userCredential = await createUserWithEmailAndPassword(
              auth,
              demoEmail,
              demoPassword
            );
            
            // Update phone number in user profile
            await updateProfile(userCredential.user, {
              displayName: formData.displayName,
              phoneNumber: formattedPhone
            });
          } else {
            throw new Error('Phone authentication failed');
          }
        } else {
          throw new Error('Invalid registration method');
        }

        // Format phone number
        const formattedPhone = registerMethod === 'phone' && phoneNumber
          ? (phoneNumber.startsWith('0') 
              ? `+66${phoneNumber.substring(1)}` 
              : phoneNumber.startsWith('+66')
              ? phoneNumber
              : `+66${phoneNumber}`)
          : null;

        // Update profile
        await setDoc(doc(db, 'users', userCredential.user.uid), {
          displayName: formData.displayName,
          email: formData.email || userCredential.user.email || null,
          phoneNumber: formattedPhone || userCredential.user.phoneNumber || null,
          role: selectedRole,
          newsletter: newsletter,
          createdAt: new Date(),
          updatedAt: new Date()
        });

        // Set role (สำหรับ Nurse App ไม่ต้องสร้างร้าน)
        await setUserRole(userCredential.user.uid, selectedRole, {
          displayName: formData.displayName,
          email: formData.email || userCredential.user.email || null,
          phoneNumber: formattedPhone || userCredential.user.phoneNumber || null
        });

        // รอสักครู่เพื่อให้ Firestore update เสร็จก่อน
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Force refresh user data จาก Firestore
        try {
          const userRef = doc(db, 'users', userCredential.user.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            console.log('✅ User role updated in Firestore:', userData.role);
            
            // ตรวจสอบว่า role ถูก update แล้วหรือยัง
            if (userData.role === selectedRole) {
              console.log('✅ Role confirmed in Firestore');
            } else {
              console.warn('⚠️ Role mismatch. Expected:', selectedRole, 'Got:', userData.role);
            }
          }
        } catch (refreshError) {
          console.error('Error refreshing user data:', refreshError);
        }

        alert(`✅ ลงทะเบียนเป็น ${getRoleDisplayName(selectedRole)} สำเร็จ!`);
        
        // Refresh AuthContext ก่อน redirect
        await refreshUser(userCredential.user);
        
        // รอสักครู่เพื่อให้ Firestore และ AuthContext update เสร็จ
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // ตรวจสอบ role อีกครั้งก่อน redirect
        const finalUserRef = doc(db, 'users', userCredential.user.uid);
        const finalUserSnap = await getDoc(finalUserRef);
        if (finalUserSnap.exists() && finalUserSnap.data().role) {
          // Refresh AuthContext อีกครั้งเพื่อให้แน่ใจว่า role ถูก update
          await refreshUser(userCredential.user);
          
          // เช็คว่ากำลังเปิดจาก LINE/Facebook หรือไม่ ถ้าใช่ให้แสดง prompt
          if (isInAppBrowser() && shouldShowBrowserPrompt() && shouldAutoRedirect()) {
            // Delay เล็กน้อยเพื่อให้หน้าเว็บโหลดเสร็จก่อน
            setTimeout(() => {
              const success = openInExternalBrowser();
              if (!success) {
                // ถ้าเปิดไม่ได้ ให้แสดง alert แนะนำ
                alert('💡 แนะนำให้เปิดใน Browser ภายนอก (Chrome, Safari) เพื่อประสบการณ์ที่ดีกว่า\n\nกรุณาคลิกที่เมนู (⋮) แล้วเลือก "เปิดใน Browser"');
              }
            }, 1000);
          }
          
          // ใช้ navigate แทน window.location.href เพื่อให้ React Router จัดการ
          navigate('/nurse', { replace: true });
        } else {
          // ถ้ายังไม่มี role ให้ reload หน้าเพื่อให้ AuthContext refresh
          window.location.href = '/nurse';
        }
      } catch (error) {
        console.error('Registration error:', error);
        let errorMessage = 'เกิดข้อผิดพลาดในการลงทะเบียน';
        
        if (error.code === 'auth/email-already-in-use') {
          errorMessage = 'อีเมลนี้ถูกใช้งานแล้ว';
        } else if (error.code === 'auth/operation-not-allowed') {
          errorMessage = 'Email/Password authentication ไม่ได้เปิดใช้งาน กรุณาติดต่อผู้ดูแลระบบ';
        } else if (error.code === 'auth/weak-password') {
          errorMessage = 'รหัสผ่านอ่อนเกินไป กรุณาใช้รหัสผ่านที่แข็งแรงกว่า';
        } else if (error.code === 'auth/invalid-email') {
          errorMessage = 'รูปแบบอีเมลไม่ถูกต้อง';
        } else {
          errorMessage = `เกิดข้อผิดพลาด: ${error.message}`;
        }
        
        alert(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  // Google Sign Up
  const handleGoogleSignUp = async () => {
    try {
      setLoading(true);
      const result = await signInWithPopup(auth, provider);
      
      if (result.user) {
        // เช็คว่า Gmail นี้เคยสมัครแล้วหรือยัง
        try {
          const userRef = doc(db, 'users', result.user.uid);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            // Gmail นี้เคยสมัครแล้ว → redirect ไปหน้า Login
            alert('อีเมลนี้เคยสมัครแล้ว กรุณาเข้าสู่ระบบ');
            // Sign out เพื่อให้ user login ใหม่
            await signOut(auth);
            navigate('/login');
            setLoading(false);
            return;
          }
        } catch (checkError) {
          console.error('Error checking user document:', checkError);
          // ถ้าเช็คไม่ได้ ให้ดำเนินการต่อ (อาจเป็น error ชั่วคราว)
        }
        
        // ตั้งค่า register method เป็น google
        setRegisterMethod('google');
        
        // เติมข้อมูลจาก Google ไปใน form
        setFormData({
          displayName: result.user.displayName || '',
          email: result.user.email || '',
          password: '', // Google sign up ไม่ต้องใช้ password
          confirmPassword: ''
        });
        
        // ไป step 2 เพื่อให้ user ตรวจสอบข้อมูลและกรอกข้อมูลเพิ่มเติม (ถ้ามี)
        setCurrentStep(2);
      }
    } catch (error) {
      console.error('Google sign up error:', error);
      let errorMessage = 'เกิดข้อผิดพลาดในการลงทะเบียนด้วย Google';
      
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'การลงทะเบียนถูกยกเลิก';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'เบราว์เซอร์บล็อก popup กรุณาอนุญาต popup';
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        errorMessage = 'มีบัญชีอื่นที่ใช้อีเมลนี้อยู่แล้ว';
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      [JOB_ROLES.NURSE]: 'พยาบาล',
      [JOB_ROLES.ASSISTANT_NURSE]: 'ผู้ช่วยพยาบาล',
      [JOB_ROLES.DOCTOR]: 'แพทย์',
      [JOB_ROLES.STAFF]: 'เจ้าหน้าที่'
    };
    return roleNames[role] || role;
  };

  const getRoleIcon = (role) => {
    const icons = {
      [JOB_ROLES.NURSE]: '🩺',
      [JOB_ROLES.ASSISTANT_NURSE]: '👩‍⚕️',
      [JOB_ROLES.DOCTOR]: '👨‍⚕️',
      [JOB_ROLES.STAFF]: '👤'
    };
    return icons[role] || '👤';
  };

  const getRoleDescription = (role) => {
    const descriptions = {
      [JOB_ROLES.NURSE]: 'จัดการเวรและอุปกรณ์',
      [JOB_ROLES.ASSISTANT_NURSE]: 'จัดการเวรและอุปกรณ์',
      [JOB_ROLES.DOCTOR]: 'จัดการเวรและอุปกรณ์',
      [JOB_ROLES.STAFF]: 'ใช้งานอุปกรณ์'
    };
    return descriptions[role] || '';
  };


  // OTP Timer
  React.useEffect(() => {
    if (otpTimer > 0) {
      const timer = setInterval(() => {
        setOtpTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [otpTimer]);

  // ตรวจสอบว่าถ้า user มี role แล้วให้ redirect ไป /nurse ทันที
  useEffect(() => {
    if (currentUser && currentUser.role) {
      // ถ้ามี role แล้ว → redirect ไป /nurse ทันที
      console.log('User already has role:', currentUser.role, 'redirecting to /nurse...');
      navigate('/nurse', { replace: true });
    }
  }, [currentUser, navigate]);

  // ตรวจสอบว่ามาจาก Google Login แต่ยังไม่ลงทะเบียน
  useEffect(() => {
    const checkGoogleSignIn = async () => {
      // ถ้ามี role แล้ว ไม่ต้องทำอะไร (จะถูก redirect โดย useEffect ด้านบน)
      if (currentUser?.role) {
        return;
      }

      // ตรวจสอบ query parameter หรือ location state
      const isComplete = new URLSearchParams(location.search).get('complete') === 'true';
      const fromGoogle = location.state?.fromGoogle;
      
      if (currentUser && (isComplete || fromGoogle)) {
        // ตรวจสอบว่ามี user document ใน Firestore หรือไม่
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const userSnap = await getDoc(userRef);
          
          if (!userSnap.exists()) {
            // ยังไม่มี user document → ไป step 2 เลย (เลือก role)
            // เติมข้อมูลจาก Google ถ้ามี
            if (location.state?.email || currentUser.email) {
              setFormData(prev => ({
                ...prev,
                displayName: location.state?.displayName || currentUser.displayName || '',
                email: location.state?.email || currentUser.email || ''
              }));
              setRegisterMethod('google');
            }
            setCurrentStep(2); // ไป step 2 (รวม Step 2+3)
          } else {
            // มี user document แล้ว → ตรวจสอบ role
            const userData = userSnap.data();
            if (userData.role) {
              // มี role แล้ว → refresh AuthContext และ redirect
              refreshUser(currentUser).then(() => {
                navigate('/nurse', { replace: true });
              });
            } else {
              // ยังไม่มี role → ไป step 2
              setCurrentStep(2);
            }
          }
        } catch (error) {
          console.error('Error checking user document:', error);
        }
      }
    };
    
    if (currentUser) {
      checkGoogleSignIn();
    }
  }, [currentUser, location, navigate, refreshUser]);

  return (
    <div className="min-h-screen flex flex-col" style={{
      background: `linear-gradient(to bottom, ${currentTheme.colors['secondary-bg']}, ${currentTheme.colors.background})`
    }}>
      {/* Header - Fixed */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md shadow-sm">
        <div className="container mx-auto px-3 md:px-4 py-2 md:py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-2">
              <img 
                src="/logo.png" 
                alt="NurseApp Logo" 
                className="w-8 h-8 md:w-10 md:h-10 object-contain transition-transform duration-300 hover:scale-110"
                style={{
                  filter: 'drop-shadow(0 2px 4px rgba(37, 99, 235, 0.2))'
                }}
                onError={(e) => {
                  // Fallback to emoji if image not found
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-blue-400 via-pink-400 to-purple-400 rounded-lg flex items-center justify-center shadow-md hidden">
                <span className="text-xl md:text-2xl">🩺</span>
              </div>
              <h1 className="text-lg md:text-xl font-bold" style={{
                background: `linear-gradient(to right, ${currentTheme.colors.primary}, ${currentTheme.colors.accent})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                NurseApp
              </h1>
            </div>

            {/* Back Link */}
            <Link 
              to="/" 
              className="inline-flex items-center text-sm md:text-base transition"
              style={{ color: currentTheme.colors['text-secondary'] }}
              onMouseEnter={(e) => {
                e.target.style.color = currentTheme.colors['text-primary'];
              }}
              onMouseLeave={(e) => {
                e.target.style.color = currentTheme.colors['text-secondary'];
              }}
            >
              <span className="text-xl md:text-2xl mr-1 md:mr-2">←</span>
              <span>กลับ</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex flex-col pt-14 md:pt-20">
        {/* Hero Section */}
        <section className="px-3 md:px-4 pt-2 md:pt-8 pb-3 md:pb-12 flex-shrink-0">
          <div className="container mx-auto">
            <div className="relative rounded-xl md:rounded-2xl overflow-hidden shadow-lg md:shadow-2xl">
              {/* Hero Background with Gradient and Vector Pattern */}
              <div 
                className="relative min-h-[400px] md:min-h-[500px] overflow-hidden py-8 md:py-12"
                style={{
                  background: `linear-gradient(135deg, #1e3a8a 0%, #3b82f6 25%, #60a5fa 50%, #93c5fd 75%, #dbeafe 100%)`
                }}
              >
                {/* Elegant Vector Pattern - Blue and White */}
                <div className="absolute inset-0 opacity-20">
                  <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 800">
                    {/* Geometric Pattern */}
                    <defs>
                      <pattern id="geometric-pattern-register" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                        <circle cx="50" cy="50" r="2" fill="white" opacity="0.3"/>
                        <circle cx="20" cy="20" r="1.5" fill="white" opacity="0.2"/>
                        <circle cx="80" cy="80" r="1.5" fill="white" opacity="0.2"/>
                        <line x1="0" y1="50" x2="100" y2="50" stroke="white" strokeWidth="0.5" opacity="0.1"/>
                        <line x1="50" y1="0" x2="50" y2="100" stroke="white" strokeWidth="0.5" opacity="0.1"/>
                      </pattern>
                      <pattern id="wave-pattern-register" x="0" y="0" width="200" height="200" patternUnits="userSpaceOnUse">
                        <path d="M0,100 Q50,50 100,100 T200,100" stroke="white" strokeWidth="1" fill="none" opacity="0.15"/>
                        <path d="M0,150 Q50,100 100,150 T200,150" stroke="white" strokeWidth="1" fill="none" opacity="0.1"/>
                      </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#geometric-pattern-register)"/>
                    <rect width="100%" height="100%" fill="url(#wave-pattern-register)"/>
                    
                    {/* Floating Circles */}
                    <circle cx="100" cy="150" r="60" fill="white" opacity="0.05"/>
                    <circle cx="1100" cy="650" r="80" fill="white" opacity="0.05"/>
                    <circle cx="600" cy="100" r="40" fill="white" opacity="0.08"/>
                    <circle cx="900" cy="300" r="50" fill="white" opacity="0.06"/>
                    <circle cx="200" cy="600" r="70" fill="white" opacity="0.04"/>
                    
                    {/* Elegant Lines */}
                    <line x1="0" y1="200" x2="1200" y2="200" stroke="white" strokeWidth="1" opacity="0.1"/>
                    <line x1="0" y1="400" x2="1200" y2="400" stroke="white" strokeWidth="1" opacity="0.1"/>
                    <line x1="0" y1="600" x2="1200" y2="600" stroke="white" strokeWidth="1" opacity="0.1"/>
                  </svg>
                </div>
                
                {/* Subtle Overlay Gradient */}
                <div className="absolute inset-0" style={{
                  background: `linear-gradient(to bottom, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 50%, rgba(0,0,0,0.1) 100%)`
                }}></div>

                {/* Floating Elements */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  <div className="absolute top-10 left-10 text-white/20 text-6xl animate-pulse">👼</div>
                  <div className="absolute top-32 right-20 text-white/20 text-5xl animate-pulse delay-1000">🩺</div>
                  <div className="absolute bottom-20 left-20 text-white/20 text-4xl animate-pulse delay-2000">❤️</div>
                  <div className="absolute bottom-32 right-10 text-white/20 text-6xl animate-pulse delay-3000">✨</div>
                </div>

                {/* Content */}
                <div className="relative z-10 flex flex-col items-center justify-center text-center px-3 md:px-4 text-white py-4 md:py-8">
                  <div className="mb-4 md:mb-6">
                    {/* Logo with enhanced styling */}
                    <div className="relative inline-block">
                      <img 
                        src="/logo.png" 
                        alt="NurseApp Logo" 
                        className="mx-auto mb-2 md:mb-4 object-contain logo-hero"
                        style={{ 
                          width: 'clamp(120px, 30vw, 200px)',
                          height: 'clamp(120px, 30vw, 200px)',
                          maxWidth: '200px',
                          maxHeight: '200px',
                          backfaceVisibility: 'hidden',
                          WebkitBackfaceVisibility: 'hidden',
                          filter: 'drop-shadow(0 10px 30px rgba(255, 255, 255, 0.4)) drop-shadow(0 0 50px rgba(37, 99, 235, 0.3))',
                          animation: 'logoFloat 6s ease-in-out infinite'
                        }}
                        onError={(e) => {
                          // Fallback to emoji if image not found
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div className="mx-auto mb-4 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center shadow-2xl border-4 border-white/30 hidden" style={{
                        width: 'clamp(120px, 30vw, 200px)',
                        height: 'clamp(120px, 30vw, 200px)',
                        maxWidth: '200px',
                        maxHeight: '200px'
                      }}>
                        <span className="text-5xl md:text-6xl lg:text-7xl">🩺</span>
                      </div>
                    </div>
                  </div>
                  <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-2 md:mb-4 drop-shadow-lg">
                    NurseApp
                  </h2>
                  <p className="text-base md:text-xl lg:text-2xl mb-2 md:mb-4 drop-shadow-md opacity-95 font-medium">
                    สร้างบัญชีใหม่
                  </p>
                  <p className="text-sm md:text-lg lg:text-xl mb-2 md:mb-4 drop-shadow-md opacity-90">
                    เริ่มต้นใช้งาน NurseApp วันนี้
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Register Form Section */}
        <section className="px-3 md:px-4 py-2 md:py-8 flex-shrink-0">
          <div className="container mx-auto">
            {/* Progress Indicator */}
            <div className="flex items-center justify-center mb-6 md:mb-8">
              {[1, 2].map((step) => (
                <React.Fragment key={step}>
                  <div className="flex items-center">
                    <div
                      className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center font-semibold text-sm md:text-base ${
                        currentStep >= step
                          ? 'text-white'
                          : 'text-gray-500'
                      }`}
                      style={{
                        backgroundColor: currentStep >= step 
                          ? currentTheme.colors.primary 
                          : currentTheme.colors['secondary-bg']
                      }}
                    >
                      {currentStep > step ? '✓' : step}
                    </div>
                  </div>
                  {step < 2 && (
                    <div
                      className={`w-12 md:w-16 h-1 mx-2`}
                      style={{
                        backgroundColor: currentStep > step 
                          ? currentTheme.colors.primary 
                          : currentTheme.colors['secondary-bg']
                      }}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>

            {/* Step 1: Choose Register Method */}
            {currentStep === 1 && (
              <div className="max-w-md mx-auto w-full">
                <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 text-center" style={{ color: currentTheme.colors['text-primary'] }}>
                  เลือกวิธีลงทะเบียน
                </h2>

                <div className="p-3 md:p-6 rounded-lg md:rounded-xl shadow-md space-y-2 md:space-y-4 mb-3 md:mb-4" style={{ backgroundColor: currentTheme.colors.background }}>
                  {/* Register Method Selection */}
                  <div className="grid grid-cols-2 gap-2 md:gap-4 mb-4 md:mb-6">
                    <button
                      type="button"
                      onClick={() => setRegisterMethod('phone')}
                      className={`p-2 md:p-4 border-2 rounded-lg transition`}
                      style={{
                        borderColor: registerMethod === 'phone' ? currentTheme.colors.primary : currentTheme.colors['border-color'],
                        backgroundColor: registerMethod === 'phone' ? currentTheme.colors['secondary-bg'] : 'transparent'
                      }}
                      onMouseEnter={(e) => {
                        if (registerMethod !== 'phone') {
                          e.target.style.borderColor = currentTheme.colors['text-secondary'];
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (registerMethod !== 'phone') {
                          e.target.style.borderColor = currentTheme.colors['border-color'];
                        }
                      }}
                    >
                      <div className="text-xl md:text-3xl mb-0.5 md:mb-2">📱</div>
                      <div className="font-semibold text-xs md:text-sm">เบอร์โทรศัพท์</div>
                      <div className="text-xs md:text-sm mt-1" style={{ color: currentTheme.colors['text-secondary'] }}>ยืนยันด้วย OTP</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setRegisterMethod('google')}
                      className={`p-2 md:p-4 border-2 rounded-lg transition`}
                      style={{
                        borderColor: registerMethod === 'google' ? currentTheme.colors.primary : currentTheme.colors['border-color'],
                        backgroundColor: registerMethod === 'google' ? currentTheme.colors['secondary-bg'] : 'transparent'
                      }}
                      onMouseEnter={(e) => {
                        if (registerMethod !== 'google') {
                          e.target.style.borderColor = currentTheme.colors['text-secondary'];
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (registerMethod !== 'google') {
                          e.target.style.borderColor = currentTheme.colors['border-color'];
                        }
                      }}
                    >
                      <div className="text-xl md:text-3xl mb-0.5 md:mb-2">📧</div>
                      <div className="font-semibold text-xs md:text-sm">Gmail</div>
                      <div className="text-xs md:text-sm mt-1" style={{ color: currentTheme.colors['text-secondary'] }}>ใช้บัญชี Google</div>
                    </button>
                  </div>
                </div>

            {/* Phone Registration */}
            {registerMethod === 'phone' && (
              <div className="bg-white p-6 rounded-xl shadow-md space-y-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">
                  ยืนยันหมายเลขโทรศัพท์
                </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  หมายเลขโทรศัพท์ *
                </label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                    +66
                  </span>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                    placeholder="0812345678"
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    disabled={otpSent}
                  />
                </div>
              </div>

              {!otpSent ? (
                <button
                  onClick={handleSendOTP}
                  disabled={loading || !phoneNumber}
                  className="w-full py-3 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
                >
                  {loading ? 'กำลังส่ง...' : 'ส่ง OTP'}
                </button>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      รหัส OTP *
                    </label>
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="123456"
                      maxLength={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-center text-2xl tracking-widest"
                    />
                    {otpTimer > 0 && (
                      <p className="text-sm text-gray-600 mt-2 text-center">
                        ⏱️ เหลือเวลา: {Math.floor(otpTimer / 60)}:{(otpTimer % 60).toString().padStart(2, '0')}
                      </p>
                    )}
                  </div>

                  <button
                    onClick={handleVerifyOTP}
                    disabled={loading || otp.length !== 6}
                    className="w-full py-3 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
                  >
                    {loading ? 'กำลังตรวจสอบ...' : 'ยืนยัน OTP'}
                  </button>

                  <button
                    onClick={() => {
                      setOtpSent(false);
                      setOtp('');
                      setOtpTimer(0);
                      setConfirmationResult(null);
                      // Clear reCAPTCHA
                      if (recaptchaVerifierRef.current) {
                        try {
                          recaptchaVerifierRef.current.clear();
                        } catch (error) {
                          console.error('Error clearing reCAPTCHA:', error);
                        }
                        recaptchaVerifierRef.current = null;
                      }
                    }}
                    className="w-full py-2 text-gray-600 hover:text-gray-800 transition"
                  >
                    เปลี่ยนหมายเลขโทรศัพท์
                  </button>
                </>
              )}
              </div>
            )}

            {/* Google Registration */}
            {registerMethod === 'google' && (
              <div className="bg-white p-6 rounded-xl shadow-md space-y-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">
                  ลงทะเบียนด้วยอีเมล
                </h3>
                <p className="text-sm text-gray-600 text-center mb-4">
                  ลงทะเบียนด้วยบัญชี Google ของคุณ
                </p>
                <button
                  onClick={handleGoogleSignUp}
                  disabled={loading}
                  className="w-full py-3 bg-white border-2 border-gray-300 rounded-lg font-semibold hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed transition flex items-center justify-center shadow-sm"
                >
                  {loading ? (
                    <span className="text-gray-600">กำลังดำเนินการ...</span>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      <span className="text-gray-700">ลงทะเบียนด้วย Google</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Personal Info + Role Selection + Store Name (รวม Step 2+3) */}
        {currentStep === 2 && (
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              ข้อมูลส่วนตัว
            </h2>
            {registerMethod === 'phone' && phoneVerified && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 text-sm">
                ✅ ยืนยันเบอร์โทรศัพท์แล้ว: {phoneNumber}
              </div>
            )}
            {registerMethod === 'google' && formData.email && (
              <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg mb-4 text-sm">
                ✅ ลงทะเบียนด้วย Google: {formData.email}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Info Section */}
              <div className="bg-white p-6 rounded-xl shadow-md space-y-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">ข้อมูลส่วนตัว</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ชื่อ-นามสกุล *
                  </label>
                  <input
                    type="text"
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    placeholder="ชื่อ-นามสกุล"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>

                {/* Email (Optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    อีเมล <span className="text-gray-500 text-xs">(ไม่บังคับ)</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="example@email.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    disabled={registerMethod === 'google' && formData.email !== ''}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    💡 สำหรับ notification และ account recovery
                  </p>
                  {registerMethod === 'google' && formData.email && (
                    <p className="text-xs text-gray-500 mt-1">อีเมลจาก Google Account</p>
                  )}
                </div>

                {/* Password (Optional) - สำหรับ Phone Registration เท่านั้น */}
                {registerMethod === 'phone' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        รหัสผ่าน <span className="text-gray-500 text-xs">(ไม่บังคับ)</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={handlePasswordChange}
                          placeholder="••••••••"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showPassword ? '👁️' : '👁️‍🗨️'}
                        </button>
                      </div>
                      {formData.password && (
                        <>
                          <div className="mt-2">
                            <div className="flex items-center space-x-1 mb-1">
                              <div className={`flex-1 h-2 rounded ${
                                passwordStrength >= 1 ? 'bg-green-500' : 'bg-gray-200'
                              }`}></div>
                              <div className={`flex-1 h-2 rounded ${
                                passwordStrength >= 2 ? 'bg-green-500' : 'bg-gray-200'
                              }`}></div>
                              <div className={`flex-1 h-2 rounded ${
                                passwordStrength >= 3 ? 'bg-green-500' : 'bg-gray-200'
                              }`}></div>
                              <div className={`flex-1 h-2 rounded ${
                                passwordStrength >= 4 ? 'bg-green-500' : 'bg-gray-200'
                              }`}></div>
                              <div className={`flex-1 h-2 rounded ${
                                passwordStrength >= 5 ? 'bg-green-500' : 'bg-gray-200'
                              }`}></div>
                            </div>
                            <p className="text-xs text-gray-600">
                              {passwordStrength < 3 ? 'รหัสผ่านอ่อน' : passwordStrength < 4 ? 'รหัสผ่านปานกลาง' : 'รหัสผ่านแข็งแรง'}
                            </p>
                          </div>
                          <div className="mt-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              ยืนยันรหัสผ่าน
                            </label>
                            <div className="relative">
                              <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={formData.confirmPassword}
                                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                placeholder="••••••••"
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 pr-10"
                              />
                              <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                              >
                                {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                              </button>
                            </div>
                            {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                              <p className="text-xs text-red-600 mt-1">รหัสผ่านไม่ตรงกัน</p>
                            )}
                          </div>
                        </>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        💡 สำหรับ backup login method (ถ้าต้องการ)
                      </p>
                    </div>
                  </>
                )}
              </div>

              {/* Role Selection */}
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">เลือกบทบาทของคุณ *</h3>
                <div className="grid grid-cols-2 gap-4">
                  {[JOB_ROLES.NURSE, JOB_ROLES.ASSISTANT_NURSE, JOB_ROLES.DOCTOR, JOB_ROLES.STAFF].map((role) => (
                    <div
                      key={role}
                      onClick={() => setSelectedRole(role)}
                      className={`p-6 border-2 rounded-xl cursor-pointer transition ${
                        selectedRole === role
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-4xl mb-3">{getRoleIcon(role)}</div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">
                          {getRoleDisplayName(role)}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {getRoleDescription(role)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Terms & Conditions */}
              <div className="bg-white p-6 rounded-xl shadow-md space-y-4">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-1 w-5 h-5 text-green-500 border-gray-300 rounded focus:ring-green-500"
                    required
                  />
                  <span className="text-sm text-gray-700">
                    ยอมรับข้อกำหนดการใช้งาน *
                  </span>
                </label>

                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newsletter}
                    onChange={(e) => setNewsletter(e.target.checked)}
                    className="mt-1 w-5 h-5 text-green-500 border-gray-300 rounded focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-700">
                    ต้องการรับข่าวสารและโปรโมชัน
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !selectedRole || !termsAccepted || !formData.displayName}
                className="w-full py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
              >
                {loading ? 'กำลังสร้างบัญชี...' : 'สร้างบัญชี'}
              </button>
            </form>
          </div>
        )}

            {/* Footer Link */}
            <div className="text-center mt-4 md:mt-8">
              <p className="text-xs md:text-sm" style={{ color: currentTheme.colors['text-secondary'] }}>
                มีบัญชีแล้ว?{' '}
                <Link 
                  to="/login" 
                  className="font-semibold transition"
                  style={{ color: currentTheme.colors.primary }}
                  onMouseEnter={(e) => {
                    e.target.style.opacity = '0.8';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.opacity = '1';
                  }}
                >
                  เข้าสู่ระบบ
                </Link>
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default RegisterNew;


